import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {tryResolveLineageStep} from 'pipelines/PipelineViewUtils';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD, FONT_PROXIMA_15} from 'styles/FontConstants';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import DataViewContext, {useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';

/**
 * Render view for empty (but SUCCEEDED) views that have no data to display.
 */
export default function EmptyView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {analysisView, steps} = dataViewContextValue;

  const stepName = useMemo(() => {
    assert(
      analysisView != null && analysisView.lineage.length > 0,
      'Expected non-empty analysisView to exist when rendering EmptyView'
    );

    const stepsArray = steps ?? [];
    const lastNode = analysisView.lineage[analysisView.lineage.length - 1];
    const resolvedStep = tryResolveLineageStep(stepsArray, lastNode);
    return resolvedStep != null ? STEP_TYPE_TO_USER_FACING_NAME[resolvedStep.stepType] : 'step';
  }, [analysisView, steps]);

  const newDataViewContextValue = useMemo(
    () => ({
      ...dataViewContextValue,
      dataTestAttrs: {component: 'EmptyView', element: 'root'},
    }),
    [dataViewContextValue]
  );

  return (
    <DataViewContext.Provider value={newDataViewContextValue}>
      <DataViewPane>
        <div className={css(styles.emptyViewWrapper)}>
          <BenchlingIcon icon="warning" className={css(styles.icon)} />
          <div className={css(styles.emptyText)}>No data generated by {stepName}</div>
        </div>
      </DataViewPane>
    </DataViewContext.Provider>
  );
}

const styles = StyleSheet.create({
  icon: {
    color: GRAY_LIGHT,
    height: '60px',
    width: '60px',
  },
  emptyViewWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    ...FONT_PROXIMA_15,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARK,
    marginTop: '10px',
  },
});
